import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Configure, Index } from 'react-instantsearch/dom';
import { connectHits, connectStateResults } from 'react-instantsearch-dom';
import { trackSearchEvent, trackAlgoliaClick } from 'ck-utils/algoliaAnalytics';
import { Button, Typography } from 'ck-ui';
import layout from 'ck-utils/layout.module.scss';
import { INDEX_COLLECTIONS, INDEX_PRODUCTS, userToken, ALGOLIA_BASE_FILTERS } from 'src/lib/algolia';
import { SearchBarDefaultProducts } from './SearchBarDefaultProducts';
import { SearchBarProductHits } from './SearchBarProductHits';
import { SearchBarCollectionHits } from './SearchBarCollectionHits';
import { SearchBarBrands } from './SearchBarBrands';
import styles from './SearchBar.module.scss';

// Main results component
const SearchBarResults = ({ open, topBrands, noResultsCollectionHandle, noResultsCollectionSort, onViewAllClick }) => {
  if (!open) return null;

  return (
    <div className={classnames('container', styles.box)}>
      <div className={styles.columns}>
        <div className={styles.columnProducts}>
          <Index indexName={INDEX_PRODUCTS}>
            <Configure
              userToken={userToken}
              hitsPerPage={6}
              filters={`${ALGOLIA_BASE_FILTERS} AND NOT tags:no-site-search`}
              analyticsTags={['web', 'search-bar']}
            />
            <SearchBarProductListContainer
              onViewAllClick={onViewAllClick}
              noResultsCollectionHandle={noResultsCollectionHandle}
              noResultsCollectionSort={noResultsCollectionSort}
            />
          </Index>
        </div>
        <div className={styles.columnCategories}>
          <Index indexName={INDEX_COLLECTIONS}>
            <Configure
              userToken={userToken}
              hitsPerPage={10}
              filters="isOnline:true AND blockOnSiteSearch:false"
              analyticsTags={['web', 'search-bar']}
            />
            <SearchBarCollectionListContainer topBrands={topBrands} />
          </Index>
        </div>
        <SearchBarMobileFooterContainer onViewAllClick={onViewAllClick} />
      </div>
    </div>
  );
};

SearchBarResults.propTypes = {
  open: PropTypes.bool,
  topBrands: PropTypes.array.isRequired,
  noResultsCollectionHandle: PropTypes.string.isRequired,
  noResultsCollectionSort: PropTypes.string.isRequired,
  onViewAllClick: PropTypes.func.isRequired,
};

SearchBarResults.defaultProps = {
  open: false
};

const SearchBarProductList = ({ isSearchStalled, searchState, searchResults, hits, noResultsCollectionHandle, noResultsCollectionSort, onViewAllClick }) => {
  const { query } = searchState;
  const hasProducts = searchResults && searchResults.nbHits !== 0;

  const renderProductHitsTitle = () => {
    if (isSearchStalled) return <strong>Searching...</strong>;
    if (hasProducts) return <strong>TOP PRODUCTS</strong>;
    if (query) {
      return (
        <>
          <span>Sorry, no results for that</span>
          <span className="is-hidden-touch has-text-weight-normal"> - Check out the below popular products</span>
          <span className={classnames('is-hidden-desktop is-block', layout.marginTop)}>POPULAR PRODUCTS</span>
        </>
      );
    }
    return <span>POPULAR PRODUCTS</span>;
  };

  const onProductClick = (isNoResults = false) => (hit, queryID, position) => e => {
    e.preventDefault();

    // Algolia analytics
    trackAlgoliaClick({
      index: isNoResults ? `${INDEX_PRODUCTS}_${noResultsCollectionSort}` : INDEX_PRODUCTS,
      eventName: 'product_click',
      queryID: hit.__queryID || queryID,
      objectID: hit.objectID,
      position: hit.__position || position
    });

    // Track GA Event
    trackSearchEvent({
      action: isNoResults
        ? 'Suggested - No Results - Product'
        : 'Suggested - Product',
      searchTerm: query,
      destinationUrl: `/products/${hit.handle}`
    });
  };

  return (
    <>
      <Typography
        className={styles.heading}
        renderAs="h3"
        variant="heading6"
      >
        {renderProductHitsTitle()}
      </Typography>
      {query && hasProducts ? (
        <SearchBarProductHits hits={hits} onProductClick={onProductClick()} />
      ) : (
        <SearchBarDefaultProducts collectionHandle={noResultsCollectionHandle} collectionSort={noResultsCollectionSort} onProductClick={onProductClick(true)} />
      )}
      <Typography
        underline
        className="is-hidden-touch"
        renderAs="a"
        href="#"
        onClick={onViewAllClick(query, hasProducts)}
        variant="heading7"
        weight={700}
      >
        {hasProducts ? `VIEW ALL RESULTS (${searchResults.nbHits})` : 'VIEW ALL NEW ARRIVALS'}
      </Typography>
    </>
  );
};

SearchBarProductList.propTypes = {
  hits: PropTypes.array.isRequired,
  noResultsCollectionHandle: PropTypes.string.isRequired,
  noResultsCollectionSort: PropTypes.string.isRequired,
  onViewAllClick: PropTypes.func.isRequired,
  searchResults: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  isSearchStalled: PropTypes.bool.isRequired
};

const SearchBarCollectionList = ({ searchState, searchResults, hits, topBrands }) => {
  const { query } = searchState;
  const hasCollections = searchResults && searchResults.nbHits !== 0;

  const onCollectionClick = (hit) => (e) => {
    e.preventDefault();

    // Algolia analytics
    trackAlgoliaClick({
      index: INDEX_COLLECTIONS,
      eventName: 'collections_click',
      type: 'collections',
      queryID: hit.__queryID,
      objectID: hit.objectID,
      position: hit.__position
    });

    // Track GA Event
    trackSearchEvent({
      action: 'Suggested - Collection',
      searchTerm: query,
      destinationUrl: `/collections/${hit.handle}`
    });
  };

  const onBrandClick = brandUrl => e => {
    e.preventDefault();
    trackSearchEvent({
      action: 'Suggested - No Results - Brand',
      searchTerm: query,
      destinationUrl: brandUrl
    });
  };

  return (
    <>
      <Typography
        className={styles.heading}
        renderAs="h3"
        variant="heading6"
      >
        {hasCollections ? 'TOP COLLECTIONS' : 'POPULAR BRANDS'}
      </Typography>
      {hasCollections && hits?.length <= 10 ? (
        <SearchBarCollectionHits hits={hits} onCollectionClick={onCollectionClick} />
      ) : (
        <SearchBarBrands brands={topBrands} onBrandClick={onBrandClick} />
      )}
    </>
  );
};

SearchBarCollectionList.propTypes = {
  hits: PropTypes.array.isRequired,
  searchResults: PropTypes.object,
  searchState: PropTypes.object.isRequired,
  topBrands: PropTypes.array.isRequired
};

SearchBarCollectionList.defaultProps = {
  searchResults: null
};

const SearchBarMobileFooter = ({ searchState, searchResults, onViewAllClick }) => {
  const { query } = searchState;
  const hasProducts = searchResults && searchResults.nbHits !== 0;
  return (
    <Button
      onClick={onViewAllClick(query, hasProducts)}
      fullwidth="all"
      className={classnames(layout.marginTop, 'is-hidden-desktop')}
    >
      {hasProducts ? 'View all results' : 'View all new arrivals'}
    </Button>
  );
};

SearchBarMobileFooter.propTypes = {
  searchResults: PropTypes.object.isRequired,
  onViewAllClick: PropTypes.func.isRequired,
  searchState: PropTypes.object.isRequired
};

const SearchBarProductListContainer = connectHits(connectStateResults(SearchBarProductList));
const SearchBarCollectionListContainer = connectHits(connectStateResults(SearchBarCollectionList));
const SearchBarMobileFooterContainer = connectStateResults(SearchBarMobileFooter);
export { SearchBarResults };
